(() => {
  // ============================================================
  // Vehicle dropdowns (Make -> Model -> Year -> Body Type) from JSON
  // - Updates the Search button to /collections/<handle>
  // - No filter params (avoids Shopify filter limits)
  // - Displays option text exactly as provided (no forced uppercase)
  // - Works with Carme select UI wrappers (NiceSelect/Bootstrap/Chosen)
  // ============================================================

  const DEFAULTS = {
    makeLabel: "Make",
    modelLabel: "Model",
    yearLabel: "Year",
    bodyLabel: "Body",

    // IMPORTANT: collections base must be Shopify collections root
    collectionsBase: "/collections"
  };

  function uniqSort(arr) {
    return Array.from(new Set((arr || []).filter(Boolean).map(v => String(v).trim())))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }

  function escapeHtml(str) {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function refreshSelectUI(selectEl) {
    try {
      if (window.jQuery && window.jQuery.fn && window.jQuery.fn.niceSelect) {
        window.jQuery(selectEl).niceSelect("update");
      }
      if (window.jQuery && window.jQuery.fn && window.jQuery.fn.selectpicker) {
        window.jQuery(selectEl).selectpicker("refresh");
      }
      if (window.jQuery && window.jQuery.fn && window.jQuery.fn.chosen) {
        window.jQuery(selectEl).trigger("chosen:updated");
      }
    } catch (e) {}
  }

  function setOptions(selectEl, placeholder, values) {
    if (!selectEl) return;

    const opts = values || [];
    selectEl.innerHTML =
      `<option value="select">${escapeHtml(placeholder || "Select")}</option>` +
      opts
        .map(v => {
          const val = String(v);
          return `<option value="${escapeHtml(val)}">${escapeHtml(val)}</option>`;
        })
        .join("");

    selectEl.disabled = opts.length === 0;
    refreshSelectUI(selectEl);
  }

  async function loadJson(url) {
    const res = await fetch(url, { cache: "no-store" });
    const text = await res.text();
    if (!res.ok) throw new Error(`Fetch failed (${res.status}) for ${url}`);
    return JSON.parse(text);
  }

  // "TOURING" => "touring", "500/595/695" => "500-595-695", "19-ON" => "19-on"
  function slugifyPart(input) {
    return String(input || "")
      .trim()
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "") // remove diacritics
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")     // anything non-alnum -> hyphen
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  }

  function buildVehicleHandle(make, model, year, body) {
    const parts = [make, model, year, body].map(slugifyPart).filter(Boolean);
    return parts.join("-");
  }

  function getConfig(component) {
    const cfg = { ...DEFAULTS };

    // if you set data-collection-base="/collections/seat-covers"
    // we still want only "/collections" for handle redirects
    const baseAttr = component.getAttribute("data-collection-base");
    if (baseAttr) {
      try {
        const u = new URL(baseAttr, window.location.origin);
        // if they gave /collections/all, keep /collections
        cfg.collectionsBase = "/collections";
      } catch (e) {
        cfg.collectionsBase = "/collections";
      }
    }

    return cfg;
  }

  async function initComponent(component) {
    if (component.dataset.ymmInit === "1") return;
    component.dataset.ymmInit = "1";

    const cfg = getConfig(component);

    const makeSel = component.querySelector(".select__make_data");
    const modelSel = component.querySelector(".select__model_data");
    const yearSel = component.querySelector(".select__year_data");

    // prefer BODY TYPE select; fallback to legacy engine/body if present
    const bodySel =
      component.querySelector(".select__body_data") ||
      component.querySelector(".select__engine_data");

    const searchBtn = component.querySelector(".search__url");

    const jsonUrl = component.getAttribute("data-vehicle-json");
    if (!jsonUrl) return;

    let raw;
    try {
      raw = await loadJson(jsonUrl);
    } catch (e) {
      // leave defaults (Search link won’t change)
      return;
    }

    if (!Array.isArray(raw)) return;

    const rows = raw
      .map(r => ({
        make: String(r.make || "").trim(),
        model: String(r.model || "").trim(),
        year: String(r.year || "").trim(),
        body: String(r.body || "").trim()
      }))
      .filter(r => r.make && r.model && r.year && r.body);

    // initial state
    setOptions(makeSel, cfg.makeLabel, uniqSort(rows.map(r => r.make)));
    setOptions(modelSel, cfg.modelLabel, []);
    setOptions(yearSel, cfg.yearLabel, []);
    setOptions(bodySel, cfg.bodyLabel, []);

    function updateSearchHref() {
      if (!searchBtn) return;

      const make = makeSel?.value;
      const model = modelSel?.value;
      const year = yearSel?.value;
      const body = bodySel?.value;

      // If incomplete selection, keep the button pointing to /collections/all
      if (
        !make || make === "select" ||
        !model || model === "select" ||
        !year || year === "select" ||
        !body || body === "select"
      ) {
        searchBtn.setAttribute("href", "/collections/all");
        return;
      }

      const handle = buildVehicleHandle(make, model, year, body);
      searchBtn.setAttribute("href", `${cfg.collectionsBase}/${handle}`);
    }

    makeSel?.addEventListener("change", () => {
      const make = makeSel.value;

      if (!make || make === "select") {
        setOptions(modelSel, cfg.modelLabel, []);
        setOptions(yearSel, cfg.yearLabel, []);
        setOptions(bodySel, cfg.bodyLabel, []);
        updateSearchHref();
        return;
      }

      const models = uniqSort(rows.filter(r => r.make === make).map(r => r.model));
      setOptions(modelSel, cfg.modelLabel, models);
      setOptions(yearSel, cfg.yearLabel, []);
      setOptions(bodySel, cfg.bodyLabel, []);
      updateSearchHref();
    });

    modelSel?.addEventListener("change", () => {
      const make = makeSel.value;
      const model = modelSel.value;

      if (!make || make === "select" || !model || model === "select") {
        setOptions(yearSel, cfg.yearLabel, []);
        setOptions(bodySel, cfg.bodyLabel, []);
        updateSearchHref();
        return;
      }

      const years = uniqSort(rows.filter(r => r.make === make && r.model === model).map(r => r.year));
      setOptions(yearSel, cfg.yearLabel, years);
      setOptions(bodySel, cfg.bodyLabel, []);
      updateSearchHref();
    });

    yearSel?.addEventListener("change", () => {
      const make = makeSel.value;
      const model = modelSel.value;
      const year = yearSel.value;

      if (!make || make === "select" || !model || model === "select" || !year || year === "select") {
        setOptions(bodySel, cfg.bodyLabel, []);
        updateSearchHref();
        return;
      }

      const bodies = uniqSort(
        rows
          .filter(r => r.make === make && r.model === model && r.year === year)
          .map(r => r.body)
      );

      setOptions(bodySel, cfg.bodyLabel, bodies);
      updateSearchHref();
    });

    bodySel?.addEventListener("change", updateSearchHref);

    // ensure wrappers update
    setTimeout(() => {
      refreshSelectUI(makeSel);
      refreshSelectUI(modelSel);
      refreshSelectUI(yearSel);
      refreshSelectUI(bodySel);
    }, 250);

    // set initial href
    updateSearchHref();
  }

  function initAll() {
    document
      .querySelectorAll("make-model-year-search[data-vehicle-json]")
      .forEach(initComponent);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();